import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, Inject, NgModule, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PressEscapeKeyDirectiveModule } from '@commons/directives/press-escape-key/press-escape-key.directive';
import {
  fadeInLeftOnEnterAnimation,
  fadeInOnEnterAnimation,
  fadeOutAnimation,
  fadeOutLeftAnimation,
} from '@commons/sticky-nav/animations';
import { DropdownNavModule } from '@commons/sticky-nav/dropdown-nav/dropdown-nav.component';
import { StickyNavTopModule } from '@commons/sticky-nav/sticky-nav-top/sticky-nav-top.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { CompaniesRoutingEnum } from '@features/companies/companies-routing.enum';
import { EmployerRoutingEnum } from '@features/employer/employer-routing.enum';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { JobsRoutingEnum } from '@features/jobs/jobs-routing.enum';
import { MENU, NavItem } from '@models/menu/menu';
import { RouterState } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { SharedModule } from '@shared/shared.module';
import { Logout } from '@stores/secure/secure.actions';
import { SecureState } from '@stores/secure/secure.state';
import { WINDOW } from '@wizbii/angular-utilities';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-sticky-nav',
  templateUrl: './sticky-nav.component.html',
  styleUrls: ['./sticky-nav.component.scss', './sticky-nav-item.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOnEnterAnimation(), fadeOutAnimation(), fadeInLeftOnEnterAnimation(), fadeOutLeftAnimation()],
})
export class StickyNavComponent implements OnDestroy {
  FeaturesRoutingEnum = FeaturesRoutingEnum;
  EmployerRoutingEnum = EmployerRoutingEnum;
  JobsRoutingEnum = JobsRoutingEnum;
  CompaniesRoutingEnum = CompaniesRoutingEnum;

  menuOpen$ = new BehaviorSubject<boolean>(false);
  triggerAnimOut$ = new BehaviorSubject<boolean>(false);

  isOnYoungOrEmployer$: Observable<boolean>;
  hideHeaderBorder$: Observable<boolean>;

  trackEvent = trackEventWithAtInternet;

  displayCompanySecureSpace$: Observable<boolean>;
  displayLocalMissionSecureSpace$: Observable<boolean>;
  companyId$: Observable<string>;
  eventId$: Observable<string>;

  MENU = MENU;

  get isMobileOrTablet$(): Observable<boolean> {
    return this.breakpointsService.isMobileAndTabletPortrait$;
  }

  get isWeb$(): Observable<boolean> {
    return this.breakpointsService.isWeb$;
  }

  constructor(
    @Inject(WINDOW) private readonly window: any,
    private readonly breakpointsService: BreakpointsService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly route: ActivatedRoute
  ) {
    this.isOnYoungOrEmployer$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        if (event.url === `/${FeaturesRoutingEnum.Young}` || event.url === `/${FeaturesRoutingEnum.Employers}`) {
          return true;
        }
        return false;
      })
    );

    this.hideHeaderBorder$ = this.store.select(RouterState.state).pipe(
      map((state: any) => state.data),
      map((data) => !!data.hideHeaderBorder),
      distinctUntilChanged()
    );

    this.displayCompanySecureSpace$ = combineLatest([
      this.route.queryParamMap.pipe(map((queryParams) => queryParams.get(SecureState.COMPANY_TOKEN))),
      this.store.select(SecureState.item(SecureState.COMPANY_TOKEN)),
      this.isWeb$.pipe(distinctUntilChanged()),
    ]).pipe(
      map(([tokenCompanyRoute, tokenCompanyStore, isWeb]) => isWeb && (!!tokenCompanyRoute || !!tokenCompanyStore))
    );

    this.displayLocalMissionSecureSpace$ = combineLatest([
      this.route.queryParamMap.pipe(map((queryParams) => queryParams.get(SecureState.LOCAL_MISSION_TOKEN))),
      this.store.select(SecureState.item(SecureState.LOCAL_MISSION_TOKEN)),
      this.isWeb$.pipe(distinctUntilChanged()),
    ]).pipe(
      map(
        ([tokenLocalMissionRoute, tokenLocalMissionStore, isWeb]) =>
          isWeb && (!!tokenLocalMissionRoute || !!tokenLocalMissionStore)
      )
    );

    this.companyId$ = this.store.select(SecureState.item(SecureState.COMPANY_ID));
    this.eventId$ = this.store.select(SecureState.item(SecureState.LOCAL_MISSION_EVENT_ID));
  }

  closeMobileMenu(): void {
    this.triggerAnimOut$.next(true);
    this.switchMobileMenu(false);
  }

  openMobileMenu(): void {
    this.triggerAnimOut$.next(false);
    this.menuOpen$.next(true);
  }

  switchMobileMenu(val: boolean): void {
    this.window.setTimeout(() => {
      this.menuOpen$.next(val);
    }, 290);
  }

  redirectOffer(): void {
    this.closeMobileMenu();
    this.router.navigate(['/', FeaturesRoutingEnum.Jobs, JobsRoutingEnum.Offer]);
  }

  logoutFromSecuredSpace(): void {
    this.store.dispatch(new Logout());
    this.router.navigate(['/']);
    this.trackEvent({ category: 'Espace Entreprise', action: 'Déconnexion', type: 'action' });
  }

  trackByIndex(index: number, _: any): number {
    return index;
  }

  trackByLabel(_: number, item: NavItem): string {
    return item.label;
  }

  ngOnDestroy(): void {
    this.menuOpen$.complete();
    this.triggerAnimOut$.complete();
  }
}

@NgModule({
  imports: [
    ReactiveFormsModule,
    SharedModule,
    DropdownNavModule,
    StickyNavTopModule,
    MatButtonModule,
    A11yModule,
    PressEscapeKeyDirectiveModule,
  ],
  declarations: [StickyNavComponent],
  exports: [StickyNavComponent],
})
export class StickyNavModule {}
